<template>
  <main class="project-page">
    <section class="project-shell">
      <!-- HEADER -->
      <app-header
        :isShow="true"
        @show-drawer="showDrawer"
        @create-visit="createProject"
        @logout="logout"
        heading="Projects"
        subHeading="Search, filter, export, and manage the project lead register."
        btnHeading="Create Project"
        :allowedCreate="userRole.create"
      />
      <!-- :allowedCreate="userRole.create" -->

      <!-- FILTER SECTION -->
      <section class="filter-card">
        <div class="filter-header">
          <div class="section-title">
            <span class="section-icon">
              <q-icon :size="$q.screen.lt.md ? 'md' : ''" name="list_alt" />
            </span>

            <div>
              <h2>Project Records</h2>
              <!-- <p>Search and manage your project records.</p> -->
            </div>
          </div>
          <div>
            <q-btn
              v-if="userRole?.export"
              no-caps
              outline
              icon="file_download"
              label="Export Excel"
              class="export-btn"
              :loading="isExporting"
              @click="exportToExcel(projects)"
            />
            <q-btn
              size="lg"
              flat
              dense
              round
              icon="refresh"
              class="refresh-btn"
              :loading="isLoading"
              @click="GetProjects"
            >
              <q-tooltip>Refresh</q-tooltip>
            </q-btn>
          </div>
        </div>

        <div v-if="$q.screen.gt.sm" class="filter-grid">
          <q-input
            v-model.trim="filters.search"
            outlined
            dense
            clearable
            label="Search"
            placeholder="Search projects, people, locations..."
            class="formField"
          >
            <template v-slot:prepend>
              <q-icon name="search" />
            </template>
          </q-input>

          <q-select
            v-model="filters.stage"
            outlined
            dense
            clearable
            emit-value
            map-options
            label="Stages"
            placeholder="All stages"
            :options="stageOptions"
            class="formField"
          />

          <q-select
            v-model="filters.city"
            outlined
            dense
            clearable
            label="Cities"
            placeholder="All cities"
            :options="cityOptions"
            class="formField"
          />
        </div>

        <div v-else class="filter-grid">
          <q-expansion-item
            v-model="filtersExpanded"
            expand-separator
            icon="filter_list"
            label="Filters"
            class="filter-expansion"
          >
            <div class="filter-grid">
              <q-input
                v-model.trim="filters.search"
                outlined
                dense
                clearable
                placeholder="Search projects, people, locations..."
                class="formField"
              >
                <template v-slot:prepend>
                  <q-icon name="search" />
                </template>
              </q-input>

              <q-select
                v-model="filters.stage"
                outlined
                dense
                clearable
                emit-value
                map-options
                placeholder="All stages"
                label="Stages"
                :options="stageOptions"
                class="formField"
              />

              <q-select
                v-model="filters.city"
                outlined
                dense
                clearable
                placeholder="All cities"
                :options="cityOptions"
                label="Cities"
                class="formField"
              />
            </div>
          </q-expansion-item>
          <q-separator />
        </div>

        <div class="filter-footer">
          <div class="record-count">
            <q-icon :size="$q.screen.lt.md ? 'md' : ''" name="description" />
            <span>
              {{ tableRows.length }}
              {{ tableRows.length === 1 ? "Project" : "Projects" }}
            </span>
          </div>

          <q-btn
            v-if="hasFilters"
            flat
            dense
            no-caps
            icon="filter_alt_off"
            label="Clear Filters"
            class="clear-filter-btn"
            @click="clearFilters"
          />
        </div>
      </section>

      <!-- ================= Q-TABLE (desktop + mobile grid) ================= -->
      <section class="list-card">
        <q-table
          ref="projectTable"
          :data="tableRows"
          :columns="columns"
          row-key="id"
          flat
          dense
          :grid="isMobile"
          :loading="isLoading"
          :filter="filters"
          :filter-method="applyFilters"
          :pagination.sync="pagination"
          :rows-per-page-options="[10]"
          :hide-pagination="true"
          class="project-q-table"
        >
          <!-- Loading state -->
          <template v-if="isLoading" v-slot:loading>
            <div class="loading-state">
              <q-spinner size="40px" color="primary" />
              <span>Loading projects...</span>
            </div>
          </template>

          <!-- Empty state -->
          <template v-if="!isLoading" v-slot:no-data>
            <div class="empty-state">
              <div class="empty-icon">
                <q-icon name="folder_off" size="38px" />
              </div>
              <h3>No projects found</h3>
              <p>There are no project records matching your filters.</p>
              <q-btn
                v-if="userRole.create"
                unelevated
                no-caps
                icon="add"
                label="Add Project"
                class="primary-button"
                @click="createProject"
              />
            </div>
          </template>

          <!-- ===== DESKTOP ROW (table mode) ===== -->
          <template v-if="!isLoading" v-slot:body="props">
            <q-tr :props="props">
              <q-td key="srNo" :props="props">
                <span class="sr-no">{{ props.row.srNo }}</span>
              </q-td>

              <q-td key="dealerName" :props="props">
                <span class="dealer-name">
                  {{ props.row.dealerName || "N/A" }}
                </span>
              </q-td>

              <q-td key="projectCity" :props="props">
                <span class="normal-value">{{
                  props.row.projectCity || "N/A"
                }}</span>
              </q-td>

              <q-td key="projectName" :props="props">
                <a
                  v-if="userRole.view"
                  href="#"
                  class="project-link"
                  @click.prevent="viewProject(props.row)"
                >
                  {{ props.row.projectName }}
                </a>
                <span v-else class="">{{ props.row.projectName }}</span>
              </q-td>

              <q-td key="projectStage" :props="props">
                <span
                  class="stage-badge"
                  :class="stageClass(props.row.projectStage)"
                  >{{ props.row.projectStage || "N/A" }}</span
                >
              </q-td>

              <q-td key="updateDate" :props="props" align="center">
                <span class="date-value">{{
                  formatDate(props.row.updateDate)
                }}</span>
              </q-td>

              <q-td key="actions" :props="props" class="actions-column">
                <div class="row-actions">
                  <q-btn
                    :disable="!userRole.view"
                    flat
                    round
                    dense
                    icon="visibility"
                    :class="{ custom_class_without_right: !userRole.view }"
                    class="view-action"
                    @click="viewProject(props.row)"
                  >
                    <q-tooltip>View</q-tooltip>
                  </q-btn>
                  <q-btn
                    :disable="!userRole.withinWindow"
                    flat
                    round
                    dense
                    icon="edit"
                    :class="{
                      custom_class_without_right: !userRole.withinWindow,
                    }"
                    class="edit-action"
                    @click="editProject(props.row)"
                  >
                    <q-tooltip>Edit</q-tooltip>
                  </q-btn>
                  <!-- <q-btn
                    :disable="!userRole.delete"
                    flat
                    round
                    dense
                    icon="delete"
                    :class="{ custom_class_without_right: !userRole.delete }"
                    class="delete-action"
                    @click="confirmDelete(props.row)"
                  >
                    <q-tooltip>Delete</q-tooltip>
                  </q-btn> -->
                </div>
              </q-td>
            </q-tr>
          </template>

          <!-- ===== MOBILE CARD (grid mode) ===== -->
          <template v-if="!isLoading" v-slot:item="props">
            <article
              class="project-card shadow-4 q-ma-sm"
              :class="{ 'is-expanded': isExpanded(props.row) }"
            >
              <!-- Header — always visible, tappable to toggle -->
              <div class="project-card-header" @click="toggleExpand(props.row)">
                <div>
                  <span class="project-label">PROJECT NO</span>
                  <strong class="project-number">{{ props.row.srNo }}</strong>
                </div>

                <div class="header-right">
                  <span
                    class="stage-badge"
                    :class="stageClass(props.row.projectStage)"
                    >{{ props.row.projectStage || "N/A" }}</span
                  >
                  <q-icon name="expand_more" class="expand-icon" size="22px" />
                </div>
              </div>

              <!-- Project name — always visible (summary info) -->
              <div class="dealer-block" @click="toggleExpand(props.row)">
                <div class="dealer-icon">
                  <q-icon size="md" name="business" />
                </div>
                <div>
                  <a
                    v-if="userRole.view"
                    href="#"
                    class="project-link"
                    @click.stop.prevent="viewProject(props.row)"
                  >
                    {{ props.row.projectName }}
                  </a>
                  <strong v-else class="">{{ props.row.projectName }}</strong>
                  <small>{{ props.row.dealerName || "No dealer" }}</small>
                </div>
              </div>

              <!-- Expandable content -->
              <q-slide-transition>
                <div v-show="isExpanded(props.row)">
                  <div class="card-details">
                    <div class="detail-item">
                      <span>Dealer</span>
                      <strong>{{ props.row.dealerName || "N/A" }}</strong>
                    </div>
                    <div class="detail-item">
                      <span>City</span>
                      <strong>{{ props.row.projectCity || "N/A" }}</strong>
                    </div>
                    <div class="detail-item">
                      <span>Created</span>
                      <strong>{{ formatDate(props.row.updateDate) }}</strong>
                    </div>
                  </div>

                  <div class="project-card-footer">
                    <q-space />
                    <q-btn
                      :disable="!userRole.view"
                      size="lg"
                      :class="{ custom_class_without_right: !userRole.view }"
                      flat
                      round
                      dense
                      icon="visibility"
                      class="view-action"
                      @click.stop="viewProject(props.row)"
                    />
                    <q-btn
                      :disable="!userRole.withinWindow"
                      size="lg"
                      flat
                      round
                      dense
                      icon="edit"
                      class="edit-action"
                      :class="{
                        custom_class_without_right: !userRole.withinWindow,
                      }"
                      @click.stop="editProject(props.row)"
                    />
                    <q-btn
                      size="lg"
                      :disable="!userRole.delete"
                      flat
                      round
                      dense
                      icon="delete"
                      class="delete-action"
                      :class="{ custom_class_without_right: !userRole.delete }"
                      @click.stop="confirmDelete(props.row)"
                    />
                  </div>
                </div>
              </q-slide-transition>
            </article>
          </template>
        </q-table>

        <!-- CUSTOM FOOTER -->
        <div
          v-if="!isLoading && tableRows.length"
          :class="isMobile ? 'mobile-pagination q-mb-lg' : 'pagination-wrapper'"
        >
          <span v-if="!isMobile" class="pagination-info">
            Showing {{ paginationStart + 1 }} - {{ paginationEnd }} of
            {{ tableRows.length }}
          </span>

          <q-pagination
            v-model="pagination.page"
            :max="totalPages"
            :max-pages="isMobile ? 5 : 6"
            direction-links
            :boundary-links="!isMobile"
            :size="$q.screen.lt.md ? 'lg' : 'md'"
          />
        </div>
      </section>
    </section>

    <!-- DELETE CONFIRMATION -->
    <q-dialog v-model="deleteDialog">
      <q-card class="delete-dialog">
        <q-card-section class="delete-dialog-header">
          <div class="delete-icon">
            <q-icon name="delete_outline" size="28px" />
          </div>
          <div>
            <h3>Delete Project?</h3>
            <p>This action cannot be undone.</p>
          </div>
        </q-card-section>

        <q-card-section class="delete-content">
          Are you sure you want to delete project
          <strong>{{ selectedProject?.projectName }}</strong
          >?
        </q-card-section>

        <q-card-actions align="right" class="delete-actions">
          <q-btn flat no-caps label="Cancel" class="cancel-btn" v-close-popup />
          <q-btn
            unelevated
            no-caps
            label="Delete Project"
            icon="delete"
            class="confirm-delete-btn"
            :loading="isDeleting"
            @click="deleteProject"
          />
        </q-card-actions>
      </q-card>
    </q-dialog>

    <drawer ref="refDrawer" />
  </main>
</template>

<script>
import { v4 as uuidV4 } from "uuid";
import axios from "axios";
import apis from "@/utilities";
import Drawer from "../layout/drawer.vue";
import api from "@/services/axios";
import appHeader from "@/views/components/header.vue";
import * as XLSX from "xlsx/xlsx.mjs";

export default {
  name: "ProjectList",
  components: { drawer: Drawer, appHeader },
  mounted() {
    this.GetProjects();
    this.getCities();
    this.getStages();
    this.setUserRole();
  },
  data() {
    return {
      isLoading: false,
      isDeleting: false,

      projects: [],
      cities: [],
      stages: [],

      filters: {
        search: "",
        stage: "",
        city: "",
      },

      pagination: {
        page: 1,
        rowsPerPage: 10,
      },

      columns: [
        {
          name: "srNo",
          label: "SR No",
          field: "srNo",
          align: "center",
          sortable: true,
        },
        {
          name: "dealerName",
          label: "Dealer Name",
          field: "dealerName",
          align: "left",
          sortable: true,
        },
        {
          name: "projectCity",
          label: "City",
          field: "projectCity",
          align: "left",
          sortable: true,
        },
        {
          name: "projectName",
          label: "Project Name",
          field: "projectName",
          align: "left",
          sortable: true,
        },
        {
          name: "projectStage",
          label: "Stage",
          field: "projectStage",
          align: "left",
        },
        {
          name: "updateDate",
          label: "Update Date",
          field: "updateDate",
          align: "left",
          sortable: true,
        },
        {
          name: "actions",
          label: "Actions",
          field: "actions",
          align: "left",
          sortable: true,
        },
      ],

      filtersExpanded: false,

      deleteDialog: false,
      selectedProject: null,
      expandedRows: [],
      userRole: {},
      isExporting: false,
    };
  },

  computed: {
    url() {
      return this.$store.getters.url;
    },

    isMobile() {
      return this.$q.screen.width < 1024;
    },

    cityOptions() {
      return this.cities.map((e) => e.cityName).filter(Boolean);
    },

    stageOptions() {
      return this.stages.map((e) => ({
        label: e.stageName,
        value: e.stageName,
      }));
    },

    hasFilters() {
      return Object.values(this.filters).some((value) => !!value);
    },

    tableRows() {
      return this.applyFilters(this.projects, this.filters).map((row) => ({
        ...row,
        srNo: row.srNo,
      }));
    },

    totalPages() {
      return Math.max(
        1,
        Math.ceil(this.tableRows.length / this.pagination.rowsPerPage)
      );
    },

    paginationStart() {
      if (!this.tableRows.length) return 0;
      return (this.pagination.page - 1) * this.pagination.rowsPerPage + 1;
    },

    paginationEnd() {
      return Math.min(
        this.pagination.page * this.pagination.rowsPerPage,
        this.tableRows.length
      );
    },
    permissions(){
        return this.$store.state.menu.permissions
    }
  },

  watch: {
    "filters.search"() {
      this.pagination.page = 1;
    },
    "filters.stage"() {
      this.pagination.page = 1;
    },
    "filters.city"() {
      this.pagination.page = 1;
    },
    "pagination.page"(val) {
      if (val > this.totalPages) this.pagination.page = 1;
    },
  },

  methods: {
    exportToExcel(rows) {
      if (!rows || !rows.length) {
        this.$q.notify({
          type: "warning",
          message: "No records to export.",
          position: "top",
        });
        return;
      }

      this.isExporting = true;

      try {
        const excelData = rows.map((row) => ({
          "Sr No": row.srNo,
          "First Visit Date": this.formatDate(row.dateOfFirstVisit),

          "Dealer ID": row.dealerId,
          "Dealer Name": row.dealerName,
          "Dealer Person Name": row.dealerPersonName,
          "Dealer City": row.dealerCity,

          "Project Name": row.projectName,
          "Project City": row.projectCity,
          "Project Description": row.projectDescription,
          "Project Stage": row.projectStage,
          "Project Product": row.projectProduct,

          Location: row.location,
          "Products Quoted": row.productsQuoted,
          "Quote Submission Date": this.formatDate(row.quoteSubmissionDate),
          "Total Quote Value": row.totalQuoteValue,

          Archived: row.isArchived,

          "Created At": this.formatDateTime(row.createdAt),
          "Created By User ID": row.createdByUserId,

          "Updated At": this.formatDateTime(row.updatedAt),
          "Updated By User ID": row.updatedByUserId,
          "Update Date": this.formatDate(row.updateDate),

          "Server Date": this.formatDateTime(row.serverDate),

          "Evidence Photo Refs": row.evidencePhotoRefs,
        }));

        const worksheet = XLSX.utils.json_to_sheet(excelData);

        // Auto column width
        const colWidths = Object.keys(excelData[0]).map((key) => {
          const maxLen = Math.max(
            key.length,
            ...excelData.map((r) => String(r[key] ?? "").length)
          );

          return {
            wch: Math.min(Math.max(maxLen + 2, 10), 45),
          };
        });

        worksheet["!cols"] = colWidths;

        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Projects");

        const fileName = `projects_${new Date()
          .toISOString()
          .substring(0, 10)}.xlsx`;

        XLSX.writeFile(workbook, fileName);

        this.$q.notify({
          type: "positive",
          message: "Excel file downloaded.",
          position: "top",
        });
      } catch (err) {
        console.error(err);

        this.$q.notify({
          type: "negative",
          message: "Unable to export Excel file.",
          position: "top",
        });
      } finally {
        this.isExporting = false;
      }
    },
    formatDateTime(date) {
      if (!date) return "";
      const d = new Date(date);
      if (Number.isNaN(d.getTime())) return "";
      const day = String(d.getDate()).padStart(2, "0");
      const month = String(d.getMonth() + 1).padStart(2, "0");
      const year = d.getFullYear();
      const hrs = String(d.getHours()).padStart(2, "0");
      const mins = String(d.getMinutes()).padStart(2, "0");
      return `${day}/${month}/${year} ${hrs}:${mins}`;
    },
    setUserRole() {
      const permissions = this.permissions;

      if (permissions) {
        permissions?.map((m) => {
          const accesstype = m.code?.split(".");
          this.userRole[accesstype[accesstype?.length - 1]] = true;
        });
        console.log("Project userRole: ", this.userRole);
      }
    },
    applyFilters(rows, terms) {
      let data = rows;

      const search = (terms.search || "").toLowerCase().trim();
      if (search) {
        data = data.filter((item) => {
          const values = [
            item.dealerName,
            item.accountCode,
            item.projectCity,
            item.projectName,
          ];
          return values
            .filter(Boolean)
            .some((value) => String(value).toLowerCase().includes(search));
        });
      }

      if (terms.stage) {
        data = data.filter((item) => item.projectStage === terms.stage);
      }

      if (terms.city) {
        data = data.filter((item) => item.projectCity === terms.city);
      }

      return data;
    },

    async GetProjects() {
      this.isLoading = true;
      this.projects = [];
      try {
        const response = await api.get(`${this.url}/${apis.GetProjectList}`);
        this.projects = response.data.data || [];
      } catch (err) {
        this.$store.dispatch("triggerUnauthorizedError", err);
        console.error(err);
      } finally {
        this.isLoading = false;
      }
    },

    async getCities() {
      try {
        const response = await axios.get(`${this.url}/${apis.GetCity}`);
        this.cities = response.data.data || [];
      } catch (err) {
        this.$store.dispatch("triggerUnauthorizedError", err);
        console.error(err);
      }
    },

    async getStages() {
      try {
        const response = await axios.get(
          `${this.url}/${apis.GetProjectStages}`
        );
        this.stages = response.data.data || [];
      } catch (err) {
        this.$store.dispatch("triggerUnauthorizedError", err);
        console.error(err);
      }
    },

    clearFilters() {
      this.filters = { search: "", stage: "", city: "" };
      this.pagination.page = 1;
    },

    createProject() {
      const { id } = this.$store.state.menu;
      this.$router.push({
        name: "projectForm",
        params: { key: `${uuidV4()}_CREATE` },
        query: { id },
      });
    },

    viewProject(project) {
      const { id } = this.$store.state.menu;
      this.$router.push({
        name: "projectForm",
        params: { key: `${project.srNo}_readOnly` },
        query: { id },
      });
    },

    editProject(project) {
      const { id } = this.$store.state.menu;
      this.$router.push({
        name: "projectForm",
        params: { key: `${project.srNo}_update` },
        query: { id },
      });
    },

    confirmDelete(project) {
      this.selectedProject = project;
      this.deleteDialog = true;
    },

    async deleteProject() {
      if (!this.selectedProject) return;
      this.isDeleting = true;
      try {
        const response = await axios.delete(
          `${this.url}/${apis.DeleteProject}/${
            this.selectedProject.id || this.selectedProject.srNo
          }`
        );
        if (response.data.success) {
          this.projects = this.projects.filter(
            (item) =>
              (item.id || item.srNo) !==
              (this.selectedProject.id || this.selectedProject.srNo)
          );
          this.$q.notify({
            type: "positive",
            message: "Project deleted successfully.",
            position: "top",
          });
          this.deleteDialog = false;
          this.selectedProject = null;
        } else {
          this.$q.notify({
            type: "negative",
            message: response.data.message || "Unable to delete project.",
            position: "top",
          });
        }
      } catch (err) {
        this.$store.dispatch("triggerUnauthorizedError", err);
        console.error(err);
        this.$q.notify({
          type: "negative",
          message: "Unable to delete project.",
          position: "top",
        });
      } finally {
        this.isDeleting = false;
      }
    },

    stageClass(stage) {
      const key = String(stage || "").toLowerCase();
      return `stage-${key || "default"}`;
    },

    formatDate(date) {
      if (!date) return "N/A";
      const value = String(date).substring(0, 10);
      const parts = value.split("-");
      if (parts.length !== 3) return value;
      return `${parts[2]}/${parts[1]}/${parts[0]}`;
    },

    exportCsv() {
      const rows = this.tableRows;
      if (!rows.length) {
        this.$q.notify({
          type: "warning",
          message: "No projects to export.",
          position: "top",
        });
        return;
      }

      const header = [
        "Sr. No",
        "Dealer Name",
        "City",
        "Project Name",
        "Stage",
        "Created",
      ];
      const lines = rows.map((row) =>
        [
          row.srNo,
          row.dealerName,
          row.projectCity,
          row.projectName,
          row.projectStage,
          this.formatDate(row.createdWhen),
        ]
          .map((val) => `"${String(val ?? "").replace(/"/g, '""')}"`)
          .join(",")
      );

      const csvContent = [header.join(","), ...lines].join("\n");
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `projects_${new Date()
        .toISOString()
        .substring(0, 10)}.csv`;
      link.click();
      URL.revokeObjectURL(link.href);
    },

    showDrawer() {
      if (this.$refs.refDrawer) {
        this.$refs.refDrawer.toggleDrawer();
      }
    },

    logout() {
      sessionStorage.setItem("token", null);
      this.$store.commit("logOutHandler");
    },

    isExpanded(row) {
      return this.expandedRows.includes(row.srNo);
    },

    toggleExpand(row) {
      console.log("row: ", row);
      const index = this.expandedRows.indexOf(row.srNo);

      if (index === -1) {
        this.expandedRows.push(row.srNo);
      } else {
        this.expandedRows.splice(index, 1);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import "../../styles/quasar.scss";

/* ============================================================
   BASE = MOBILE FIRST
   ============================================================ */

.project-page {
  min-height: 100vh;
  padding: 0;
  color: $primary;
  text-align: left;
}

.project-shell {
  max-width: 100%;
  margin: 0 auto;
  // position: relative;
}

/* ================= FILTER ================= */

.filter-card {
  padding: 20px 18px 15px;
  background: #fff;
  border-bottom: 1px solid #e5edef;
}

.filter-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 18px;
}

.section-title {
  display: flex;
  align-items: center;
  gap: 12px;
}

.section-icon {
  width: 38px;
  height: 38px;
  display: grid;
  place-items: center;
  color: $primary;
  background: #e7f2f4;
  border-radius: 10px;
}

.section-title h2 {
  margin: 0;
  color: $primary;
  font-size: 17px;
}

.section-title p {
  margin: 3px 0 0;
  color: #73858a;
  font-size: 15px;
}

.refresh-btn {
  color: $primary;
}

.filter-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 12px;
}

.formField :deep(.q-field__control) {
  background: #fff;
}

.filter-footer {
  min-height: 40px;
  margin-top: 12px;
  display: flex;
  align-items: center;
}

.record-count {
  display: flex;
  align-items: center;
  gap: 6px;
  color: #73858a;
  font-size: 15px;
}

.record-count .q-icon {
  color: $primary;
}

.clear-filter-btn {
  margin-left: auto;
  color: $primary;
}

/* ================= LIST CARD WRAPPER ================= */

.list-card {
  background: #fff;
  overflow: hidden;
}

/* ================= Q-TABLE OVERRIDES ================= */

.project-q-table {
  :deep(.q-table__card) {
    box-shadow: none;
    background: transparent;
  }

  :deep(.q-table__middle) {
    box-shadow: none;
  }

  :deep(table) {
    width: 100%;
    min-width: 950px;
    border-collapse: collapse;
  }

  :deep(thead tr th) {
    height: 50px;
    padding: 0 14px;
    color: #73858a;
    background: #f7fafb;
    border-bottom: 1px solid #e3ebed;
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 0.04em;
    text-align: left;
    text-transform: uppercase;
    white-space: nowrap;
  }

  :deep(tbody td) {
    height: 72px;
    padding: 8px 14px;
    color: #35525a;
    border-bottom: 1px solid #edf2f3;
    font-size: 13px;
    vertical-align: middle;
  }

  :deep(tbody tr:hover) {
    background: #f9fbfb;
  }

  :deep(.q-table__grid-content) {
    padding: 0;
    display: block;
    background: #f5f8f9;
  }

  :deep(.q-table__grid-item) {
    padding: 0;
    margin-bottom: 12px;
    width: 100%;
  }
}

/* ================= SHARED CELL CONTENT ================= */

.sr-no {
  color: #526a70;
}

.dealer-name {
  color: #35525a;
}

.normal-value {
  color: #35525a;
}

.project-link {
  color: $primary;
  font-weight: 600;
  text-decoration: none;
}

.project-link:hover {
  text-decoration: underline;
}

.date-value {
  color: #526a70;
  white-space: nowrap;
}

.stage-badge {
  display: inline-block;
  padding: 5px 10px;
  border-radius: 6px;
  font-size: 11px;
  font-weight: 600;
  white-space: nowrap;
  color: $primary;
  background: #e7f2f4;
}

.stage-new {
  color: #1c6ea4;
  background: #e3f1fb;
}

.stage-inprogress,
.stage-in-progress {
  color: #b4790f;
  background: #fdf1de;
}

.stage-won {
  color: #2f8a4a;
  background: #e6f5ea;
}

.stage-lost {
  color: #c45151;
  background: #fceeee;
}

.text-center,
.actions-column {
  // text-align: center !important;
}

.row-actions {
  display: flex;
  justify-content: center;
  gap: 2px;
}

.view-action {
  color: $primary;
}

.edit-action {
  color: $primary;
}

.delete-action {
  color: #c45151;
}

/* ================= PAGINATION ================= */

.pagination-wrapper {
  min-height: 65px;
  padding: 12px 24px;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 25px;
  border-top: 1px solid #edf2f3;
}

.pagination-info {
  margin-right: auto;
  color: #87969a;
  font-size: 12px;
}

.mobile-pagination {
  min-height: 60px;
  padding: 10px;
  display: flex;
  justify-content: center;
  background: transparent;
  margin-bottom: 20px !important;
}

/* ================= LOADING / EMPTY ================= */

.loading-state,
.empty-state {
  min-height: 300px;
  width: 100%;
  padding: 40px 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
}

.loading-state {
  gap: 12px;
  color: #73858a;
  font-size: 13px;
}

.empty-icon {
  width: 72px;
  height: 72px;
  margin-bottom: 16px;
  display: grid;
  place-items: center;
  color: $primary;
  background: #e7f2f4;
  border-radius: 50%;
}

.empty-state h3 {
  margin: 0;
  color: $primary;
  font-size: 18px;
}

.empty-state p {
  margin: 6px 0 20px;
  color: #819297;
  font-size: 13px;
}

.primary-button {
  color: #fff;
  background: $primary-gradient;
  border-radius: 8px;
}

/* ================= MOBILE CARD (grid item) ================= */

.project-card {
  padding: 16px;
  background: #fff;
  border: 1px solid #e3ebed;
  border-radius: 14px;
  box-shadow: 0 4px 14px rgba(27, 67, 77, 0.05);
  transition: box-shadow 0.2s ease;
}

.project-card-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding-bottom: 14px;
  border-bottom: 1px solid #edf2f3;
  cursor: pointer;
}

.header-right {
  display: flex;
  align-items: center;
  gap: 6px;
}

.project-card.is-expanded {
  box-shadow: 0 6px 20px rgba(27, 67, 77, 0.1);
}

.expand-icon {
  color: #87969a;
  transition: transform 0.25s ease;
}

.project-card.is-expanded .expand-icon {
  transform: rotate(180deg);
  color: #146b80;
}

.project-label {
  display: block;
  margin-bottom: 3px;
  color: #8a9a9e;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.08em;
}

.project-number {
  color: $primary;
  font-size: 15px;
}

.dealer-block {
  margin: 14px 0;
  display: flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
}

.dealer-icon {
  width: 40px;
  height: 40px;
  display: grid;
  place-items: center;
  color: $primary;
  background: #e7f2f4;
  border-radius: 9px;
}

.dealer-block small {
  display: block;
  color: #819297;
  font-size: 12px;
}

.card-details {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 13px;
  padding-top: 14px;
  border-top: 1px solid #edf2f3;
}

.detail-item {
  display: flex;
  flex-direction: column;
  gap: 3px;
}

.detail-item span {
  color: #91a1a5;
  font-size: 12px;
}

.detail-item strong {
  color: rgba(0, 0, 0, 0.87);
  font-size: 15px;
  font-weight: 400;
}

.project-card-footer {
  margin-top: 15px;
  padding-top: 10px;
  display: flex;
  align-items: center;
  border-top: 1px solid #edf2f3;
}

.project-card-footer .q-btn {
  min-width: 40px;
  min-height: 40px;
}

/* ================= DELETE DIALOG ================= */

.delete-dialog {
  width: 420px;
  max-width: calc(100vw - 30px);
  border-radius: 14px;
}

.delete-dialog-header {
  display: flex;
  align-items: center;
  gap: 12px;
}

.delete-icon {
  width: 44px;
  height: 44px;
  display: grid;
  place-items: center;
  color: #c45151;
  background: #fceeee;
  border-radius: 10px;
}

.delete-dialog-header h3 {
  margin: 0;
  color: $primary;
  font-size: 17px;
}

.delete-dialog-header p {
  margin: 3px 0 0;
  color: #87969a;
  font-size: 12px;
}

.delete-content {
  color: #526a70;
  font-size: 13px;
}

.delete-content strong {
  color: $primary;
}

.delete-actions {
  padding: 12px 16px 16px;
}

.cancel-btn {
  color: #55757d;
}

.confirm-delete-btn {
  color: #fff;
  background: #c45151;
  border-radius: 7px;
}
.custom_class_without_right {
  color: gray !important;
}

/* ============================================================
   TABLET (>= 641px)
   ============================================================ */

@media (min-width: 641px) {
  .project-page {
    padding: 24px 16px;
  }

  .filter-card {
    padding: 25px 34px 18px;
  }

  .filter-header {
    margin-bottom: 22px;
  }

  .section-title h2 {
    font-size: 19px;
  }

  .section-title p {
    font-size: 15px;
  }

  .filter-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 14px;
  }

  .filter-footer {
    margin-top: 15px;
  }

  .record-count {
    font-size: 12px;
  }
}

/* ============================================================
   SMALL DESKTOP / LARGE TABLET (>= 800px)
   ============================================================ */

@media (min-width: 800px) {
  .filter-grid {
    grid-template-columns: repeat(1, minmax(0, 1fr));
  }
}

/* ============================================================
   DESKTOP (>= 1024px)
   ============================================================ */

@media (max-width: 1024px) {
  .filter-grid {
    grid-template-columns: repeat(1, minmax(0, 1fr));
  }
}

@media (min-width: 1024px) {
  .filter-grid {
    grid-template-columns: 2fr 1fr 1fr;
  }

  .pagination-wrapper {
    justify-content: flex-end;
  }
}

@media (min-width: 1201px) {
  .project-shell {
    max-width: 80vw;
    margin: 0 auto;
  }

  .pagination-wrapper {
    justify-content: flex-end;
  }
}
</style>